import { EMessengerEventBusEvents } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from 'stores/RootStore';
import { ESideOverlayType } from 'stores/SideOverlay/SideOverlayStore';
import { DeviceTypes, useDeviceType } from 'utils/hooks/useDeviceType';
import { useEmbedded } from 'utils/hooks/useEmbedded';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';
import { buildDealMessengerChatSource } from 'utils/messenger/buildDealMessengerChatSource';
import { computeErpMainMessengerHostState } from 'utils/messenger/computeErpMainMessengerHostState';
import { isDealDetailPathname } from 'utils/messenger/isDealDetailPathname';
import { resolveDealIdForMessengerHostOverlays } from 'utils/messenger/resolveDealIdForMessengerHostOverlays';

/**
 * When leaving a deal detail URL, close the compact column messenger only. Classic overlay stays open (EUO-191).
 */
export const useCloseCompactMessengerWhenLeavingDealRoute = () => {
  const store = useStore();
  const location = useLocation();
  const { isEmbedded } = useEmbedded();
  const deviceType = useDeviceType();
  const { setEventBusRef, sendEvent } = useMessengerEventBus();
  const prevOnDealDetailRef = useRef<boolean | null>(null);

  const isMobile = deviceType === DeviceTypes.MOBILE && !isEmbedded;

  useEffect(() => {
    const onDealDetail = isDealDetailPathname(location.pathname);
    const prev = prevOnDealDetailRef.current;

    if (prev === true && !onDealDetail) {
      const { sideOverlayStore, previewsStore } = store;

      if (!sideOverlayStore.isOpen || sideOverlayStore.context.type !== ESideOverlayType.Messenger) {
        prevOnDealDetailRef.current = onDealDetail;

        return;
      }

      const dealIdForThread = resolveDealIdForMessengerHostOverlays(location.pathname, store);

      const liveDealThreadChatSource =
        dealIdForThread != null && dealIdForThread !== ''
          ? buildDealMessengerChatSource({
              pathname: location.pathname,
              search: location.search,
              dealId: dealIdForThread,
              store,
            })
          : null;

      const { showCompactColumnMessenger } = computeErpMainMessengerHostState(store, {
        isMobile,
        isEmbedded,
        setEventBusRef,
        amplitudeLogger,
        liveDealThreadChatSource,
      });

      if (showCompactColumnMessenger) {
        sendEvent?.(EMessengerEventBusEvents.CLOSE_MESSENGER, {});
        previewsStore.closePreview();
        sideOverlayStore.closeSideOverlay();
      }
    }

    prevOnDealDetailRef.current = onDealDetail;
  }, [location.pathname, location.search, store, isMobile, isEmbedded, setEventBusRef, sendEvent]);
};
